/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { Ban, CheckCircle2, Scale, Trophy } from 'lucide-react';
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { api, isApiError, type ApprovalDecisionRequest, type ApprovalRequest, type ApprovalStatus, type NewApproval } from '../../api';
import {
  AUCTION_APPROVAL_RESOURCE,
  AUCTION_OVERRIDE_ACTION,
  type AuctionBid,
  type AuctionDetail,
  type AuctionOverrideAction,
  type AuctionOverrideRequest,
  type AuctionParticipant,
  type AuctionReasonRequest,
} from '../../api/areas/market';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Section, type Column } from '../../components/blocks';
import { BigFigure, FactRows, GateList, HeroCard, Timeline, TwoColumn } from '../../components/flow';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { CorrelationLink, DomainLink, PlainId } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { useRetained, useWrite, WriteError } from '../../components/Write';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { useLiveEvents } from '../../lib/useLiveEvents';
import { AuctionTimingButtons } from '../operator/RegistryMarket';
import { RefreshButton } from '../registry/shared';
import { AUCTION_STATUS, CLOSING_SOON_MS, Countdown, CustomerRef, decimalGte, isOver, LiveDot, MarketBadge, marketEnum, money, useNow } from './shared';
import { policyLabel } from '@/lib/labels';

type Panel = 'cancel' | 'override' | null;

function CancelDialog({ a, open, onDone, onClose }: { a: AuctionDetail; open: boolean; onDone: () => void; onClose: () => void }) {
  const { t } = useTranslation('market');
  const id = useId();
  const [reason, setReason] = useState('');
  const write = useWrite((req: AuctionReasonRequest, opts) => api.cancelAuction(a.id, req, opts));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim() && (await write.run({ reason: reason.trim() }))) onDone();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !write.busy && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('cancel.title')}</DialogTitle>
            <DialogDescription>{t('cancel.description')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-reason`}>{t('reasonRequired')}</Label>
            <Textarea id={`${id}-reason`} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} disabled={write.busy} maxLength={1000} required />
          </div>
          {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={write.busy}>
              {t('back')}
            </Button>
            <Button type="submit" variant="destructive" disabled={write.busy || !reason.trim()}>
              <Ban /> {write.busy ? t('cancel.busy') : t('cancel.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalDecision({ a, onDecided }: { a: ApprovalRequest; onDecided: (s: ApprovalStatus) => void }) {
  const { t } = useTranslation('market');
  const [reason, setReason] = useState('');
  const write = useWrite((req: ApprovalDecisionRequest, opts) => api.decideApproval(a.id, req, opts));
  const decide = async (approve: boolean) => {
    const res = await write.run({ approve, reason: reason.trim() });
    if (res) onDecided(res.status);
  };
  const asked = (a.payload as { action?: string } | null)?.action === 'AWARD' ? t('override.award') : t('override.void');
  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="font-medium">{asked}</span>
        <span className="text-muted-foreground">{t('override.requestedBy')}</span>
        <EntityRef label={a.requested_by_name} id={a.requested_by} />
        <span className="text-muted-foreground" title={formatDateTime(a.requested_at)}>
          {formatRelative(a.requested_at)}
        </span>
      </div>
      <p className="text-sm">“{a.reason}”</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="text"
          className="h-8 min-w-48 flex-1"
          aria-label={t('override.decisionReason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('override.decisionReason')}
          disabled={write.busy}
        />
        <Button type="button" size="sm" onClick={() => decide(true)} disabled={write.busy || !reason.trim()}>
          {t('override.approve')}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => decide(false)} disabled={write.busy || !reason.trim()}>
          {t('override.reject')}
        </Button>
      </div>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
    </div>
  );
}

function Step({ n, title, body, done, children }: { n: number; title: ReactNode; body?: ReactNode; done?: boolean; children: ReactNode }) {
  return (
    <li className="relative flex gap-3 pb-6 last:pb-0">
      <span className="absolute top-8 bottom-0 left-3.5 w-px bg-border last:hidden" aria-hidden="true" />
      <span
        className={cn(
          'z-10 flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
          done ? 'border-success bg-success text-white' : 'border-primary/40 bg-accent text-accent-foreground',
        )}
      >
        {done ? <CheckCircle2 className="size-4" aria-hidden="true" /> : n}
      </span>
      <div className="min-w-0 flex-1 space-y-3">
        <div>
          <div className="font-semibold">{title}</div>
          {body ? <div className="text-sm text-muted-foreground">{body}</div> : null}
        </div>
        {children}
      </div>
    </li>
  );
}

/** Four-eyes: request an `auction.result.override` approval, a second operator decides, then execute it. */
function OverrideDialog({ a, open, onDone, onClose }: { a: AuctionDetail; open: boolean; onDone: () => void; onClose: () => void }) {
  const { t } = useTranslation('market');
  const id = useId();
  const accepted = a.bids.filter((b) => b.status === 'ACCEPTED' && b.id !== a.winner_bid_id);
  const approvals = useApi((signal) => api.listApprovals({ signal }), []);
  const pending =
    approvals.status === 'success' ? approvals.data.items.filter((x) => x.action === AUCTION_OVERRIDE_ACTION && x.resource_type === AUCTION_APPROVAL_RESOURCE && x.resource_id === a.id) : [];
  const [what, setWhat] = useState<'VOID' | 'AWARD'>('VOID');
  const [bidId, setBidId] = useState(accepted.at(-1)?.id ?? '');
  const [requestReason, setRequestReason] = useState('');
  const [approvalId, setApprovalId] = useState('');
  const [reason, setReason] = useState('');
  const request = useWrite((req: NewApproval, opts) => api.createApproval(req, opts));
  const execute = useWrite((req: AuctionOverrideRequest, opts) => api.overrideAuction(a.id, req, opts));

  const payload: AuctionOverrideAction = what === 'VOID' ? { action: 'VOID' } : { action: 'AWARD', bid_id: bidId };
  const submitRequest = async (e: FormEvent) => {
    e.preventDefault();
    if (!requestReason.trim() || (what === 'AWARD' && !bidId)) return;
    const res = await request.run({ action: AUCTION_OVERRIDE_ACTION, resource_type: AUCTION_APPROVAL_RESOURCE, resource_id: a.id, payload, reason: requestReason.trim() });
    if (res) approvals.reload();
  };
  const submitExecute = async (e: FormEvent) => {
    e.preventDefault();
    if (approvalId.trim() && reason.trim() && (await execute.run({ approval_request_id: approvalId.trim(), reason: reason.trim() }))) onDone();
  };
  const busy = request.busy || execute.busy;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="size-5" aria-hidden="true" />
            {t('override.title')}
          </DialogTitle>
          <DialogDescription>{t('override.description')}</DialogDescription>
        </DialogHeader>
        <ol className="mt-2">
          <Step n={1} title={t('override.step1')} body={t('override.step1Body')} done={request.state.phase === 'done'}>
            <form className="grid gap-3 sm:grid-cols-2" onSubmit={submitRequest}>
              <div className="grid gap-1.5">
                <Label htmlFor={`${id}-what`}>{t('override.what')}</Label>
                <Select value={what} onValueChange={(v) => setWhat(v as 'VOID' | 'AWARD')} disabled={request.busy}>
                  <SelectTrigger id={`${id}-what`} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VOID">{t('override.void')}</SelectItem>
                    <SelectItem value="AWARD" disabled={accepted.length === 0}>
                      {t('override.award')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {what === 'AWARD' ? (
                <div className="grid gap-1.5">
                  <Label htmlFor={`${id}-bid`}>{t('override.bid')}</Label>
                  <Select value={bidId} onValueChange={setBidId} disabled={request.busy}>
                    <SelectTrigger id={`${id}-bid`} className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {accepted.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          #{b.sequence} · {money(b.amount, b.currency)} · {b.bidder_name ?? t('auction.bidder')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <div className="grid gap-1.5 sm:col-span-2">
                <Label htmlFor={`${id}-rr`}>{t('reasonRequired')}</Label>
                <Input id={`${id}-rr`} type="text" value={requestReason} onChange={(e) => setRequestReason(e.target.value)} disabled={request.busy} />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" size="sm" variant="outline" disabled={request.busy || !requestReason.trim() || (what === 'AWARD' && !bidId)}>
                  {request.busy ? t('override.requesting') : t('override.request')}
                </Button>
              </div>
            </form>
            {request.state.phase === 'error' ? <WriteError error={request.state.error} /> : null}
            {request.state.phase === 'done' ? (
              <p className="flex flex-wrap items-center gap-1.5 text-sm text-success">
                <CheckCircle2 className="size-4" aria-hidden="true" /> {t('override.requested')} <CopyText value={request.state.result.id} />
                <span className="text-foreground">{t('override.requestedNext')}</span>
              </p>
            ) : null}
          </Step>
          <Step n={2} title={t('override.step2')} body={t('override.step2Body')} done={Boolean(approvalId)}>
            {approvals.status === 'loading' ? <Loading /> : null}
            {approvals.status === 'error' ? <DataError error={approvals.error} onRetry={approvals.reload} /> : null}
            {approvals.status === 'success' && pending.length === 0 ? <p className="text-sm text-muted-foreground">{t('override.noPending')}</p> : null}
            <div className="space-y-2">
              {pending.map((x) => (
                <ApprovalDecision
                  key={x.id}
                  a={x}
                  onDecided={(s) => {
                    if (s === 'APPROVED') setApprovalId(x.id);
                    approvals.reload();
                  }}
                />
              ))}
            </div>
          </Step>
          <Step n={3} title={t('override.step3')} body={t('override.step3Body')}>
            <form className="grid gap-3" onSubmit={submitExecute}>
              <div className="grid gap-1.5">
                <Label htmlFor={`${id}-aid`}>{t('override.approvalId')}</Label>
                <Input id={`${id}-aid`} type="text" className="font-mono" value={approvalId} onChange={(e) => setApprovalId(e.target.value)} disabled={execute.busy} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor={`${id}-er`}>{t('override.executeReason')}</Label>
                <Textarea id={`${id}-er`} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={execute.busy} />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
                  {t('back')}
                </Button>
                <Button type="submit" disabled={execute.busy || !approvalId.trim() || !reason.trim()}>
                  {execute.busy ? t('override.executing') : t('override.execute')}
                </Button>
              </DialogFooter>
            </form>
            {execute.state.phase === 'error' ? <WriteError error={execute.state.error} /> : null}
          </Step>
        </ol>
      </DialogContent>
    </Dialog>
  );
}

/** `/auctions/:id`: terms, result, every bid (accepted and rejected), participants and the history. */
export function AuctionPage() {
  const { t } = useTranslation('market');
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getAuction(id, { signal }), [id]);
  const a = useRetained(result.status === 'success' ? result.data : undefined);
  // New bids, extensions and closing appear without pressing Refresh.
  const liveState = useLiveEvents(id ? `/auctions/${encodeURIComponent(id)}/live` : null, () => result.reload());
  const [panel, setPanel] = useState<Panel>(null);
  const now = useNow(1000);
  useDocumentTitle(a ? t('auction.docTitle', { domain: a.domain_name }) : t('auction.title'));
  const crumbs = [{ label: t('auctions.title'), to: '/auctions' }, { label: a?.domain_name ?? '…' }];

  if (!a) {
    return (
      <>
        <PageHeader title={t('auction.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError error={result.error} onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload} title={t('auction.loadError')} />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }

  const cancellable = a.status === 'SCHEDULED' || a.status === 'OPEN' || a.status === 'CLOSED';
  const done = () => {
    setPanel(null);
    result.reload();
  };
  const winner = a.bids.find((b) => b.id === a.winner_bid_id);
  const bids = [...a.bids].sort((x, y) => x.decided_at.localeCompare(y.decided_at));
  // `bid_count` counts accepted bids only, so a missing one means the list was capped.
  const allBidsShown = bids.filter((b) => b.status === 'ACCEPTED').length >= a.bid_count;
  const live = a.status === 'OPEN';
  const closingSoon = live && new Date(a.ends_at).getTime() - now <= CLOSING_SOON_MS;
  const shownPrice = a.final_price ?? a.current_price;
  const reserveMet = a.reserve_price !== null && a.current_price !== null && decimalGte(a.current_price, a.reserve_price);
  const actor = (type: string) => marketEnum('actorType', type);
  // The auction is finished: its top bid is the highest one received, not a leader.
  const over = isOver(a.status);

  const bidColumns: Column<AuctionBid>[] = [
    { key: 'seq', header: t('auction.seq'), numeric: true, cell: (b) => b.sequence ?? '—' },
    { key: 'bidder', header: t('auction.bidder'), className: 'min-w-36 max-w-48 whitespace-normal', cell: (b) => <CustomerRef id={b.bidder_customer_id} name={b.bidder_name} /> },
    {
      key: 'amount',
      header: t('auction.amount'),
      numeric: true,
      cell: (b) => <span className={cn('whitespace-nowrap', b.status === 'ACCEPTED' ? 'font-semibold' : 'text-muted-foreground line-through')}>{money(b.amount, b.currency)}</span>,
    },
    { key: 'min', header: t('auction.minimum'), numeric: true, cell: (b) => <span className="whitespace-nowrap text-muted-foreground">{money(b.minimum_amount, b.currency)}</span> },
    {
      key: 'outcome',
      header: t('auction.outcome'),
      cell: (b) =>
        b.status === 'ACCEPTED' ? (
          b.id === a.winner_bid_id ? (
            <Badge tone="ok">
              <Trophy aria-hidden="true" /> {t('auction.bidWinner')}
            </Badge>
          ) : (
            <Badge tone="info">{b.id === a.highest_bid_id ? (over ? t('auction.bidHighest') : t('auction.bidLeading')) : t('auction.bidAccepted')}</Badge>
          )
        ) : (
          <Badge tone="warn" title={b.reject_reason ?? undefined}>
            {t('auction.bidRejected')}
            {b.reject_reason ? ` · ${marketEnum('rejectReason', b.reject_reason)}` : ''}
          </Badge>
        ),
    },
    {
      key: 'decided',
      header: t('auction.decided'),
      cell: (b) => (
        <span className="text-xs whitespace-nowrap" title={t('auction.received', { at: formatDateTime(b.received_at) })}>
          {formatDateTime(b.decided_at)}
        </span>
      ),
    },
    { key: 'client', header: t('auction.clientBidId'), className: 'max-w-32 whitespace-normal', cell: (b) => <code className="text-xs">{b.client_bid_id}</code> },
  ];

  const participantColumns: Column<AuctionParticipant>[] = [
    { key: 'customer', header: t('auction.customer'), className: 'max-w-36 whitespace-normal', cell: (p) => <CustomerRef id={p.customer_id} name={p.customer_name} /> },
    {
      key: 'subject',
      header: t('auction.registrant'),
      className: 'max-w-32 whitespace-normal',
      cell: (p) => <EntityRef label={p.subject_name} id={p.subject_id} to={`/subjects/${encodeURIComponent(p.subject_id)}`} />,
    },
    {
      key: 'backorder',
      header: t('auction.backorder'),
      className: 'max-w-24 whitespace-normal',
      cell: (p) =>
        p.backorder_id ? (
          <EntityRef label={t('auction.openBackorder')} id={p.backorder_id} to={`/backorders/${encodeURIComponent(p.backorder_id)}`} />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    { key: 'joined', header: t('auction.joined'), cell: (p) => <span className="text-xs whitespace-nowrap">{formatDateTime(p.joined_at)}</span> },
  ];

  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            {live ? <LiveDot className="size-3" /> : null}
            {t('au.detail.title', { domain: a.domain_name })}
          </span>
        }
        crumbs={crumbs}
        subtitle={t('au.detail.subtitle', { kind: marketEnum('auctionKind', a.kind), id: a.id.slice(0, 8) })}
        actions={
          <>
            {cancellable ? (
              <Button type="button" variant="outline" size="sm" onClick={() => setPanel('cancel')}>
                <Ban /> {t('auction.cancel')}
              </Button>
            ) : null}
            {a.status === 'CLOSED' ? (
              <Button type="button" variant="outline" size="sm" onClick={() => setPanel('override')}>
                <Scale /> {t('auction.override')}
              </Button>
            ) : null}
            {liveState === 'live' || liveState === 'reconnecting' ? (
              <Badge tone={liveState === 'live' ? 'ok' : 'pending'}>{liveState === 'live' ? t('auction.liveOn') : t('auction.liveReconnecting')}</Badge>
            ) : null}
            <RefreshButton onClick={result.reload} />
            <AuctionTimingButtons auction={a} onChanged={result.reload} />
          </>
        }
      />
      {panel === 'cancel' ? <CancelDialog a={a} open onDone={done} onClose={() => setPanel(null)} /> : null}
      {panel === 'override' ? <OverrideDialog a={a} open onDone={done} onClose={() => setPanel(null)} /> : null}

      <TwoColumn
        main={
          <>
            <HeroCard
              className={cn(live && 'border-success/40', closingSoon && 'border-warning/50')}
              kicker={`${marketEnum('auctionKind', a.kind)} · ${t('au.detail.rules', { version: a.policy_version })}`}
              title={a.domain_name}
              subtitle={a.kind === 'PUBLIC' ? t('auction.public') : t('auction.private')}
              badge={
                <span className="flex flex-wrap items-center gap-1.5">
                  <MarketBadge group="auctionStatus" map={AUCTION_STATUS} value={a.status} />
                  {closingSoon ? <Badge tone="warn">{t('countdown.closingSoon')}</Badge> : null}
                </span>
              }
            >
              <div className="grid grid-cols-1 gap-5 border-t pt-4 sm:grid-cols-3">
                <BigFigure label={a.final_price ? t('auction.finalPrice') : t('auction.currentPrice')}>{money(shownPrice ?? a.start_price, a.currency)}</BigFigure>
                <BigFigure label={live ? t('countdown.endsIn') : a.status === 'SCHEDULED' ? t('countdown.startsIn') : t('auction.ends')}>
                  {live ? (
                    <Countdown to={a.ends_at} now={now} prefix={false} className={cn(closingSoon && 'text-warning')} />
                  ) : a.status === 'SCHEDULED' ? (
                    <Countdown to={a.starts_at} now={now} prefix={false} />
                  ) : (
                    <span className="text-base" title={formatDateTime(a.ends_at)}>
                      {formatDateTime(a.ends_at)}
                    </span>
                  )}
                </BigFigure>
                <BigFigure label={a.winner_customer_id ? t('auction.winner') : over ? t('auction.highest') : t('auction.leader')}>
                  <span className="text-base">
                    {a.winner_customer_id ? (
                      <CustomerRef id={a.winner_customer_id} name={a.winner_name} />
                    ) : a.highest_bidder_id ? (
                      <CustomerRef id={a.highest_bidder_id} name={a.leader_name} />
                    ) : (
                      <span className="text-muted-foreground">{over ? t('auction.noWinner') : t('auctions.noBidder')}</span>
                    )}
                  </span>
                  {winner ? <div className="text-xs font-normal text-muted-foreground">{t('auction.winningBid')} #{winner.sequence}</div> : null}
                </BigFigure>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                {t('auctions.bidCount', { count: a.bid_count })}
                {a.extension_count > 0 ? ` · ${t('auctions.extended', { count: a.extension_count })}` : ''}
              </p>
            </HeroCard>
            {a.status_reason ? <Callout tone="info" title={t('auction.statusReason')}>{a.status_reason}</Callout> : null}

            <Section
              flush
              title={t('auction.bidsTitle')}
              description={allBidsShown ? t('auction.bidsDesc') : t('auction.bidsTruncated', { shown: bids.length, count: a.bid_count })}
            >
              <DataTable columns={bidColumns} rows={bids} rowKey={(b) => b.id} empty={<EmptyState title={t('auction.noBids')} />} />
            </Section>

            <Section title={t('au.detail.handover')}>
              <KeyValue
                columns={2}
                rows={[
                  [t('auction.bidsAccepted'), a.bid_count],
                  [t('auction.closed'), a.closed_at ? formatDateTime(a.closed_at) : null],
                  [t('auction.settled'), a.settled_at ? formatDateTime(a.settled_at) : null],
                  [t('auction.order'), a.order_id ? <EntityRef key="o" label={t('auction.openOrder')} id={a.order_id} to={`/orders/${encodeURIComponent(a.order_id)}`} /> : null],
                  [t('auction.domain'), a.domain_id ? <DomainLink key="dm" keyOrName={a.domain_name} /> : null],
                  [t('auction.drop'), a.drop_id ? <EntityRef key="d" label={a.domain_name} id={a.drop_id} to={`/drops/${encodeURIComponent(a.drop_id)}`} /> : null],
                  [t('auction.overrideApproval'), a.override_approval_id ? <PlainId key="ov" id={a.override_approval_id} /> : null],
                ]}
              />
            </Section>

            <Section flush title={t('auction.participants')}>
              <DataTable columns={participantColumns} rows={a.participants} rowKey={(p) => p.customer_id} empty={<EmptyState title={t('auction.openToAll')} />} />
            </Section>
          </>
        }
        side={
          <>
            <Section title={t('au.detail.terms')}>
              <FactRows
                rows={[
                  [t('auction.startPrice'), money(a.start_price, a.currency)],
                  [
                    t('auction.reserve'),
                    a.reserve_price === null ? (
                      t('auction.noReserve')
                    ) : (
                      <span key="r" className="inline-flex flex-wrap items-center justify-end gap-1.5">
                        {money(a.reserve_price, a.currency)}
                        {reserveMet ? <Badge tone="ok">{t('auction.reserveMet')}</Badge> : <Badge tone="warn">{t('auction.reserveNotMet')}</Badge>}
                      </span>
                    ),
                  ],
                  [t('auction.starts'), formatDateTime(a.starts_at)],
                  [t('auction.ends'), `${formatDateTime(a.ends_at)} (${formatRelative(a.ends_at)})`],
                  a.original_ends_at !== a.ends_at && [t('auction.originalEnd'), formatDateTime(a.original_ends_at)],
                  [t('auction.extensions'), a.extension_count],
                  [t('au.detail.policy'), policyLabel(a.policy_key, a.policy_version)],
                  [t('auction.policyHash'), <CopyText key="p" value={a.policy_sha256} display={`${a.policy_sha256.slice(0, 12)}…`} />],
                  [t('auction.createdBy'), a.created_by_name ? <EntityRef key="cb" label={a.created_by_name} id={a.created_by_id} /> : actor(a.created_by_type)],
                ]}
              />
            </Section>
            <Section title={t('au.detail.settlement')}>
              <GateList
                gates={[
                  { id: 'closed', title: t('au.flow.closed'), hint: a.closed_at ? formatDateTime(a.closed_at) : undefined, ok: a.closed_at ? true : null },
                  { id: 'winner', title: t('au.flow.winner'), hint: a.winner_name ?? undefined, ok: a.winner_bid_id ? true : a.status === 'UNSOLD' || a.status === 'CANCELLED' ? false : null },
                  {
                    id: 'paid',
                    title: t('au.flow.paid'),
                    hint: a.capture_entry_id ? t('au.flow.paidHint') : undefined,
                    ok: a.capture_entry_id || a.events.some((e) => e.event_type === 'PAYMENT_SETTLED') ? true : a.status === 'FAILED' ? false : null,
                  },
                  {
                    id: 'assigned',
                    title: t('au.flow.assigned'),
                    hint: a.domain_id ? a.domain_name : undefined,
                    ok: a.domain_id || a.events.some((e) => e.event_type === 'DOMAIN_ASSIGNED') ? true : a.status === 'FAILED' ? false : null,
                  },
                ]}
              />
            </Section>
            <Section
              title={t('auction.history')}
              description={a.events.length < a.event_count ? t('auction.historyTruncated', { shown: a.events.length, count: a.event_count }) : t('auction.historyDesc', { count: a.event_count })}
            >
              <Timeline
                items={[...a.events].reverse().map((e) => ({
                  id: e.id,
                  tone: e.event_type.includes('REJECT') || e.event_type.includes('FAIL') || e.event_type.includes('CANCEL') ? 'warn' : 'info',
                  title: <span title={e.event_type}>{marketEnum('auctionEvent', e.event_type)}</span>,
                  time: <time dateTime={e.occurred_at}>{formatDateTime(e.occurred_at)}</time>,
                  meta: (
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      {e.actor_name ? <EntityRef label={e.actor_name} id={e.actor_id} /> : <span>{actor(e.actor_type)}</span>}
                      {e.correlation_id ? <CorrelationLink id={e.correlation_id} label={t('auction.correlation')} /> : null}
                    </span>
                  ),
                  // Only a rejected bid carries a coded reason; everywhere else the reason is
                  // what an operator typed, and it is shown exactly as they typed it.
                  body: typeof e.payload.reason === 'string' ? (e.event_type === 'BID_REJECTED' ? marketEnum('rejectReason', e.payload.reason) : e.payload.reason) : undefined,
                }))}
              />
            </Section>
          </>
        }
      />
    </>
  );
}
